import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import API from '../utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    // Seed initial state dari localStorage (cache, bukan sumber kebenaran)
    try {
      const saved = localStorage.getItem('ki_user');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      // Cookie dikirim otomatis via withCredentials — tidak perlu cek localStorage
      const { data } = await API.get('/auth/me');
      setUser(data.user);
      localStorage.setItem('ki_user', JSON.stringify(data.user));
    } catch {
      // Cookie tidak ada / expired
      localStorage.removeItem('ki_token');
      localStorage.removeItem('ki_user');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const _persist = (token, userData) => {
    // Simpan token di localStorage hanya sebagai fallback untuk API client
    if (token) localStorage.setItem('ki_token', token);
    localStorage.setItem('ki_user', JSON.stringify(userData));
    setUser(userData);
  };

  const login = async (email, password, rememberMe = false) => {
    const { data } = await API.post('/auth/login', { email, password, rememberMe });
    // Jika butuh OTP, jangan persist — kembalikan flag agar UI minta kode
    if (data.needsOtp) return { needsOtp: true, email: data.email };
    _persist(data.token, data.user);
    return { user: data.user };
  };

  const verifyOtp = async (email, otp, rememberMe = false) => {
    const { data } = await API.post('/auth/verify-otp', { email, otp, rememberMe });
    _persist(data.token, data.user);
    return data.user;
  };

  const register = async (name, email, password) => {
    const { data } = await API.post('/auth/register', { name, email, password });
    return data;
  };

  const googleSignIn = async (credential) => {
    const { data } = await API.post('/auth/google', { credential });
    _persist(data.token, data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await API.post('/auth/logout'); // clear httpOnly cookie di server
    } catch { /* ignore */ }
    localStorage.removeItem('ki_token');
    localStorage.removeItem('ki_user');
    setUser(null);
  };

  const updateUser = (updates) => {
    const updated = { ...user, ...updates };
    setUser(updated);
    localStorage.setItem('ki_user', JSON.stringify(updated));
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, verifyOtp, register, googleSignIn, logout, updateUser, fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
